<template>
  <div class="note-detail">
    <div class="title-box primary-box">
      <h2>{{ isNew ? 'Nuova Nota' : (note?.title ?? "") }}</h2>
    </div>
    <div class="note-toolbar">
      <v-icon @click="goBack">mdi-arrow-left-top</v-icon>
      <v-icon @click="saveNote">{{ isEditing ? 'mdi-content-save' : 'mdi-pencil' }}</v-icon>
      <v-icon v-if="isEditing && note?.id" @click="cancelEdit" >mdi-close</v-icon>
      <v-icon color="red" v-if="note?.id" @click="deleteNote">mdi-delete</v-icon>
    </div>
    

    <div v-if="!isEditing && note" class="view-form">
      <div v-html="note.content" class="wysiwyg-content article-content-container" />
    </div>

    <div class="edit-form" v-else>
      <v-text-field
          v-model="editableNote.title"
          label="Titolo"
          density="compact"
          hide-details
      />
      <QuillEditor 
          theme="snow"
          v-model:content="editableNote.content"
          contentType="html"
          toolbar="minimal"
          class="quill-editor"
      />
    </div>

    <!-- FILES SECTION -->
    <div class="note-files" v-if="note?.fileIds?.length">
      <div class="file-list">
        <div v-for="file in note.fileIds" :key="file.filePath" class="file-item">
          <div v-if="isEditing">
            <v-icon @click="removeFile(file.filePath)" :disabled="uploading" color="red">mdi-delete</v-icon>
            <span style="margin-right: 10px">{{ file.fileName }}</span>
          </div>
          <div v-else>
            <h4>{{file.fileName}}</h4>
            <template v-if="getFileType(file) === 'video'">
              <video :src="getFileUrl(file.filePath)" controls class="video-player" />
            </template>
            <template v-else-if="getFileType(file) === 'audio'">
              <audio :src="getFileUrl(file.filePath)" controls class="audio-player"/>
            </template>
            <template v-else>
              <a :href="getFileUrl(file.filePath)" target="_blank">{{ file.filePath.split('/').pop() }}</a>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- FILE UPLOAD -->
    <div v-if="isEditing && !isNew" class="upload-box">
      <input type="file" @change="handleFileUpload" />
      <span v-if="uploading">Caricamento...</span>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/ApiService';
import Note from '@/entities/Note';
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  name: 'NoteDetail',
  components: {
    QuillEditor
  },
  data() {
    return {
      note: null,
      editableNote: {
        title: '',
        content: ''
      },
      isEditing: false,
      uploading: false
    };
  },
  computed: {
    mainFolderId() {
      return this.$route.params.notebookId;
    },
    path() {
      return decodeURIComponent(this.$route.params.encPath);
    },
    noteId() {
      return this.$route.params.id || null;
    },
    isNew() {
      return !this.noteId;
    },
    parentPath() {
      const pathParts = this
          .path
          .split('/')
          .filter(x => !!x);
      
      if (pathParts.length === 0 || pathParts.length === 1)
        return  '/';
      
      return '/' + pathParts.slice(0, -1).join('/');
    },
    folderId() {
      const pathParts = this
          .path
          .split('/')
          .filter(x => !!x);

      if (pathParts.length === 0)
        return  this.mainFolderId;

      return pathParts.pop();
    }
  },
  async mounted() {
    await this.$nextTick();
    await this.fetchNote();
  },
  methods: {
    async fetchNote() {
      if (!this.isNew) {
        try {
          this.$store.dispatch('setLoading', true);
          const data = await apiService.getNote(this.mainFolderId, this.path, this.noteId);
          this.note = new Note(data);
          this.editableNote.title = this.note.title;
          this.editableNote.content = this.note.content;
        } catch (err) {
          console.error('Errore nel caricamento della nota:', err);
        } finally {
          this.$store.dispatch('setLoading', false);
        }
      } else {
        this.note = new Note({
          id: null,
          title: '',
          content: '',
          createdAt: new Date(),
          updatedAt: new Date(),
          path: this.path,
          comments: [],
          tags: [],
          fileIds: []
        });
        this.startEditing();
      }
    },
    startEditing() {
      this.isEditing = true;
    },
    cancelEdit() {
      this.editableNote.title = this.note.title;
      this.editableNote.content = this.note.content;
      this.isEditing = false;
    },
    goBack() {
      if(this.folderId === this.mainFolderId) {
        this.$router.push(`/notebook/${this.mainFolderId}`);
        return;
      }      
      
      this.$router.push(`/notebook/${this.mainFolderId}/folder/${encodeURIComponent(this.parentPath)}/${this.folderId}`);
    },
    async saveNote() {
      if(!this.isEditing) {
        this.startEditing();
        return;
      }
      
      try {
        this.$store.dispatch('setLoading', true);
        const updatedData = {
          ...this.note,
          title: this.editableNote.title,
          content: this.editableNote.content,
          updatedAt: new Date()
        };
        
        if (this.isNew) {
          const createdNoteId = await apiService.createNote(this.mainFolderId, this.path, updatedData);
          this.$router.push(`/notebook/${this.mainFolderId}/note/${encodeURIComponent(this.path)}/${createdNoteId}`);
        } else {
          await apiService.updateNote(this.mainFolderId, this.path, this.noteId, updatedData);
          this.note = updatedData;
          this.isEditing = false;
        }
      } catch (err) {
        console.error('Errore nel salvataggio:', err);
      } finally {
        this.$store.dispatch('setLoading', false);
      }
    },
    async deleteNote() {
      const confirmDelete = confirm('Sei sicuro di voler eliminare questa nota?');
      if (!confirmDelete) return;

      try {
        this.$store.dispatch('setLoading', true);
        console.log(this.parentPath, ' - ', this.folderId)
        await apiService.deleteNote(this.mainFolderId, this.path, this.noteId);        
        
        this.goBack();
        
      } catch (err) {
        console.error('Errore durante l\'eliminazione:', err);
      } finally {
        this.$store.dispatch('setLoading', false);
      }
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file || !this.noteId) return;

      try {
        this.$store.dispatch('setLoading', true);
        this.uploading = true;
        await apiService.uploadFileToNote(this.mainFolderId, this.path, this.noteId, file);
        await this.fetchNote()
      } catch (err) {
        console.error('Errore durante l\'upload:', err);
      } finally {
        this.$store.dispatch('setLoading', false);
        this.uploading = false;
      }
    },
    async removeFile(filePath) {
      try {
        await apiService.deleteFileFromNote(this.mainFolderId, this.path, this.noteId, filePath);
        this.note.fileIds = this.note.fileIds.filter(f => f.filePath !== filePath);
      } catch (err) {
        console.error('Errore durante la rimozione del file:', err);
      }
    },
    getFileType(file) {
      if (file.contentType.startsWith('video')) return 'video';
      if (file.contentType.startsWith('audio')) return 'audio';
      return 'other';
    },
    getFileUrl(filePath) {
      return `https://cinopedia.s3.eu-west-1.amazonaws.com/${filePath}`;
    }
  },
  watch: {
    $route() {
      this.fetchNote();
    },
    '$store.state.user'() {
      this.fetchNote();
    }
  }
};
</script>

<style scoped lang="scss">
@import "../style/_variables.scss";

.note-detail {
  text-align: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;


  .edit-form {
    margin-top: 20px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    .v-text-field {
      width: 100%;
    }
  }
  .view-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.note-toolbar {
  display: flex;
  justify-content: flex-end;
  border: 1px solid $primary-color;
  gap: 10px;
  margin-top: 5px;
  background: var(--secondary-background-color);
  padding: 10px;
  border-radius: 5px;
}

.wysiwyg-content {
  text-align: left;
  padding: 10px;
  width: 100%;
  background-color: var(--secondary-background-color);
  border: 1px solid $primary-color;
  h1, h2 {
    text-align: center;
  }
}

.note-files {
  margin-top: 1rem;
}

.file-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: var(--secondary-background-color);
  border-top: 1px solid $primary-color;
  border-bottom: 1px solid $primary-color;
}

.upload-box {
  margin-top: 1rem;
}

.video-player {
  width: 100%;
  min-width: 300px;
}
.audio-player {
  padding: 5px;
  max-width: 100%;
  width: 100vw;
  min-width: 300px;
}
</style>
